/**
 * Renders the grocery list as plain text for pasting into a message.
 *
 * Kept free of React and formatting concerns so it can be unit-tested: the
 * caller passes already-formatted item labels ("2 tomatoes"), grouped under
 * the same category headings the screen shows.
 *
 * Ticked items are left out — the point of sharing the list is telling
 * someone what still needs buying. Categories that empty out as a result
 * disappear rather than leaving a bare heading.
 */

/** Plain-text bullet. Hyphen + space survives every SMS/chat client intact. */
const BULLET = "- ";

/**
 * @param {object}   args
 * @param {string}   [args.title]        Schedule name, used in the header.
 * @param {number}   [args.avgServings]  Average servings per recipe.
 * @param {Array}    args.sections       [{ emoji, label, items: string[] }]
 * @param {string[]} [args.cupboardItems] Staples not yet stocked — also need buying.
 * @returns {string} Empty string when nothing is left to buy.
 */
export function buildGroceryListText({ title, avgServings, sections, cupboardItems = [] }) {
  const blocks = [];

  (sections || []).forEach((section) => {
    const items = (section.items || []).filter(Boolean);
    if (items.length === 0) return;
    const heading = section.emoji ? `${section.emoji} ${section.label}` : section.label;
    blocks.push([heading, ...items.map((i) => BULLET + i)].join("\n"));
  });

  const cupboard = (cupboardItems || []).filter(Boolean);
  if (cupboard.length > 0) {
    blocks.push(["🧂 Cupboard — not stocked yet", ...cupboard.map((i) => BULLET + i)].join("\n"));
  }

  if (blocks.length === 0) return "";

  const header = [title ? `Grocery list — ${title}` : "Grocery list"];
  if (avgServings) {
    header.push(`About ${avgServings} serving${avgServings === 1 ? "" : "s"} per recipe`);
  }

  return [header.join("\n"), ...blocks].join("\n\n");
}

export default buildGroceryListText;
